import nanoajax from 'nanoajax';
import { deepClone } from 'utils/general';

/**
 * Here, we provide an easy to use, Promise-based
 * interface for communicating with the Shopify Cart
 * API.
 *
 * Examples:
 * import cart from 'lib/cart'
 *
 * cart
 *  .get()
 *  .then(data => {
 *    console.log(data)
 *  })
 *
 * cart
 *  .add({id: variantID, quantity: 1})
 *  .then(data => {
 *    console.log(data)
 *  })
 *
 * cart
 *  .update({id: variantID, quantity: 1})
 *  .then(data => {
 *    console.log(data)
 *  })
 */
class Cart {
  get() {
    return new Promise((resolve) => {
      nanoajax.ajax(
        {
          url: `${window.Shopify.routes.root}cart.js?v=${Date.now()}`,
          method: 'get',
        },
        (code, responseText, request) => {
          const cart = JSON.parse(responseText);
          resolve(cart);
        },
      );
    });
  }

  generateShopifyCartBody(
    mode,
    { id, quantity, properties = {}, subscription = false },
  ) {
    const props = Object.keys(properties)
      .reduce((arr, key) => {
        let sanitizedProperty = properties[key];
        if (typeof sanitizedProperty === 'string') {
          sanitizedProperty = properties[key].replace('&plus;', '%2B');

          if (key === 'promo_text') {
            sanitizedProperty = properties[key];
          }
        }
        if (typeof sanitizedProperty !== 'undefined') {
          arr.push(`properties[${key}]=${sanitizedProperty}`);
        }

        return arr;
      }, [])
      .join('&');
    if (mode === 'add') {
      return `quantity=${quantity}&id=${id}${props ? '&' + props : ''}${
        subscription ? '&selling_plan=' + subscription : ''
      }`;
    } else {
      return `updates[${id}]=${quantity}${props ? '&' + props : ''}${
        subscription ? '&selling_plan=' + subscription : ''
      }`;
    }
  }

  update(data) {
    return new Promise((resolve, reject) => {
      nanoajax.ajax(
        {
          url: window.Shopify.routes.root + 'cart/update.js',
          method: 'post',
          body: this.generateShopifyCartBody('update', data),
        },
        (code, responseText, request) => {
          const cart = JSON.parse(responseText);
          if (Number(code) !== 200) {
            cart.errors = [cart.description];
          }
          resolve(cart);
        },
      );
    });
  }

  change(data) {
    return new Promise((resolve, reject) => {
      nanoajax.ajax(
        {
          url: window.Shopify.routes.root + 'cart/change.js',
          method: 'post',
          body: this.generateShopifyCartBody('add', data),
        },
        (code, responseText, request) => {
          const cart = JSON.parse(responseText);
          if (Number(code) !== 200) {
            cart.errors = [cart.description];
          }
          resolve(cart);
        },
      );
    });
  }

  /**
   * Adds a product to the Shopify cart
   *
   * @param {Object} data {id: <id>, quantity: <Number>}
   */
  add(data) {
    return new Promise((resolve) => {
      nanoajax.ajax(
        {
          url: window.Shopify.routes.root + 'cart/add.js',
          method: 'post',
          body: this.generateShopifyCartBody('add', data),
        },
        (code, responseText, request) => {
          const cart = JSON.parse(responseText);
          if (Number(code) !== 200) {
            cart.errors = [cart.description];
          }
          resolve(cart);
        },
      );
    });
  }

  /**
   * Adds a product to the Shopify cart
   *
   * @param {Object} data {id: <id>, quantity: <Number>}
   * @param {Boolean} skipGWP skip adding these products as GWP
   */
  async multiple(data, skipGWP = false) {
    const oldCart = await this.get();
    const promises = [];
    let alreadyAdded = false;
    const cloneData = deepClone(data);
    cloneData.array.map((singleData, index) => {
      singleData.quantity = 1;
      singleData.id = singleData.original || singleData.id;
      alreadyAdded =
        oldCart.items.find((item) => item.variant_id === singleData.id) ||
        false;
      if (!alreadyAdded) {
        if (!skipGWP) {
          singleData.properties
            ? (singleData.properties._is_gwp = true)
            : (singleData.properties = { _is_gwp: true });
        }
        promises.push(
          new Promise((resolve, reject) => {
            setTimeout(() => {
              nanoajax.ajax(
                {
                  url: window.Shopify.routes.root + 'cart/add.js',
                  method: 'post',
                  body: this.generateShopifyCartBody('add', singleData),
                },
                (code, responseText, request) => {
                  const cart = JSON.parse(responseText);
                  if (Number(code) !== 200) {
                    cart.errors = [cart.description];
                  }
                  resolve(cart);
                },
              );
            }, 500 * index);
          }),
        );
      }
    });

    return Promise.all(promises).then((values) => {
      return values;
    });
  }

  /**
   * Adds a product to the Shopify cart
   *
   * @param {Object} data {id: <id>, quantity: <Number>}
   */
  async cleanMultiple(products) {
    const promises = [];
    products.array.map((prod, index) => {
      promises.push(
        new Promise((resolve, reject) => {
          setTimeout(() => {
            nanoajax.ajax(
              {
                url: window.Shopify.routes.root + 'cart/add.js',
                method: 'post',
                body: this.generateShopifyCartBody('add', prod),
              },
              (code, responseText, request) => {
                const cart = JSON.parse(responseText);
                if (Number(code) !== 200) {
                  cart.errors = [cart.description];
                }
                resolve(cart);
              },
            );
          }, 500 * index);
        }),
      );
    });

    return Promise.all(promises).then((values) => {
      return values;
    });
  }
}

export default new Cart();
